import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../../app/components/ui/dialog";
import { Button } from "../../../../app/components/ui/button";
import { Input } from "../../../../app/components/ui/input";
import { Alert, AlertDescription } from "../../../../app/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../app/components/ui/select";

type Values = {
  id?: string;
  name: string;
  parent_id: string | null;
};

type Props = {
  open: boolean;
  mode: "create" | "edit";
  initialValues: Values;
  parentOptions?: Array<{ id: string; name: string }>;
  error?: string | null;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: Values) => Promise<void>;
};

export function CategoryFormModal({ open, mode, initialValues, parentOptions, error, onOpenChange, onSubmit }: Props) {
  const [name, setName] = useState(initialValues.name);
  const [parentId, setParentId] = useState<string | null>(initialValues.parent_id);
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    setName(initialValues.name);
    setParentId(initialValues.parent_id);
    setLocalError(null);
  }, [initialValues]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (!name.trim()) {
      setLocalError("Name is required.");
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({ ...initialValues, name: name.trim(), parent_id: parentId });
    } finally {
      setSubmitting(false);
    }
  };

  const title = mode === "create" ? "Add category" : "Edit category";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground" htmlFor="category-name">
                Name
              </label>
              <Input
                id="category-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Electronics"
                autoFocus
              />
            </div>

            {parentOptions && parentOptions.length > 0 ? (
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Parent category</label>
                <Select
                  value={parentId ?? "__root__"}
                  onValueChange={(v) => setParentId(v === "__root__" ? null : v)}
                >
                  <SelectTrigger className="min-h-9 w-full">
                    <SelectValue placeholder="Select parent (optional)" />
                  </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__root__">No parent (root)</SelectItem>
                  {parentOptions.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="hidden md:block" aria-hidden />
            )}
          </div>

          {(localError || error) && (
            <Alert variant="destructive">
              <AlertDescription>{localError || error}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

